import React, { FC, useState } from 'react'
import moment from 'moment'
import 'moment/min/locales'
import { useTranslation } from 'react-i18next'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { languages } from '../../../config/languages'
import {
    getCurrentCustomerEntity,
    getCurrentProvider,
    getCustomerEntities,
    setCurrentCustomerEntity,
    setCurrentUserRoles,
} from '../../../helpers/functions/manageSessionStorageData'
import { Providers } from '../../../interfaces/Providers'
import { logout } from '../../../redux/slices/session/session'
import { GENERAL_LOGIN_PATH, GENERAL_SETTINGS } from '../../../config/pathList'
import { useTypedDispatch } from '../../../redux/store'
import { useHistory } from 'react-router-dom'
import { CustomerEntity } from '../../../httpClient/interfaces/CustomerEntity'

const useStyles = makeStyles(() => ({
    selected: {
        fontWeight: 'bold',
    },
    subMenu: {
        display: 'flex',
        flexDirection: 'column',
    },
    returnIcon: {
        width: '60px',
        marginBottom: '10px',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            cursor: 'pointer',
        },
    },
}))

const SHOW_ACCOUNTS = 'accounts'
const SHOW_LANGUAGE = 'language'

interface PropsFormatMenu {
    setSelectedMenu: (selectedMenu: string) => void
    children?: React.ReactNode
}

const FormatMenu: FC<PropsFormatMenu> = ({ setSelectedMenu, children }) => {
    const classes = useStyles()

    return (
        <div className={classes.subMenu}>
            <ArrowBackIcon
                classes={{ root: classes.returnIcon }}
                fontSize="small"
                onClick={() => setSelectedMenu('')}
            />
            {children}
        </div>
    )
}

const AccountsMenu: FC = () => {
    const classes = useStyles()
    const [selectedAccount, setSelectedAccount] = useState(getCurrentCustomerEntity().customerForEntityId)
    const customerEntities = getCustomerEntities()

    const changedAccounts = (newSelectedAccount: CustomerEntity) => {
        const { customerForEntityId, externalRoleDescription } = newSelectedAccount

        setSelectedAccount(customerForEntityId)
        setCurrentUserRoles(externalRoleDescription)
        setCurrentCustomerEntity(newSelectedAccount)
    }

    return (
        <>
            {customerEntities.map((customerEntity) => (
                <MenuItem
                    className={selectedAccount === customerEntity.customerForEntityId ? classes.selected : ''}
                    key={customerEntity.customerForEntityId}
                    onClick={() => changedAccounts(customerEntity)}
                >
                    {customerEntity.description}
                </MenuItem>
            ))}
        </>
    )
}

const LanguageMenu: FC = () => {
    const { i18n } = useTranslation()
    const classes = useStyles()

    const selectedLanguage = i18n.language || i18n.languages[0]

    const changeLanguage = (languageCode) => {
        moment.locale(languageCode)
        i18n.changeLanguage(languageCode)
    }

    return (
        <>
            {languages.map((language) => (
                <MenuItem
                    className={selectedLanguage === language.code ? classes.selected : ''}
                    key={language.code}
                    onClick={() => changeLanguage(language.code)}
                >
                    {language.localeName}
                </MenuItem>
            ))}
        </>
    )
}

interface PropsDisplayMenu {
    selectedMenu: string
    setSelectedMenu: (selectedMenu: string) => void
}

const DisplayMenu = React.forwardRef<HTMLButtonElement, PropsDisplayMenu>(({ selectedMenu, setSelectedMenu }) => {
    const { t } = useTranslation()
    const dispatch = useTypedDispatch()
    const history = useHistory()
    const isTempo = getCurrentProvider() === Providers.tempo

    const handleLogout = () => {
        dispatch(logout())
        history.push(GENERAL_LOGIN_PATH)
    }

    const handleSettings = () => {
        history.push(GENERAL_SETTINGS)
    }

    switch (selectedMenu) {
        case SHOW_LANGUAGE: {
            return (
                <FormatMenu setSelectedMenu={setSelectedMenu}>
                    <LanguageMenu />
                </FormatMenu>
            )
        }
        case SHOW_ACCOUNTS: {
            return (
                <FormatMenu setSelectedMenu={setSelectedMenu}>
                    <AccountsMenu />
                </FormatMenu>
            )
        }
        default: {
            return (
                <>
                    <MenuItem onClick={() => setSelectedMenu(SHOW_LANGUAGE)}>
                        {t('navigation.top_bar.language')}
                    </MenuItem>
                    {isTempo && (
                        <MenuItem onClick={() => setSelectedMenu(SHOW_ACCOUNTS)}>
                            {t('navigation.top_bar.accounts')}
                        </MenuItem>
                    )}
                    {!isTempo && <MenuItem onClick={handleSettings}>{t('navigation.top_bar.setting')}</MenuItem>}
                    <MenuItem onClick={handleLogout}>{t('navigation.top_bar.logout')}</MenuItem>
                </>
            )
        }
    }
})

interface Props {
    anchorEl: HTMLButtonElement
    setAnchorEl: (any) => void
}

const MenuTopBar: FC<Props> = ({ anchorEl = null, setAnchorEl }) => {
    const [selectedMenu, setSelectedMenu] = useState('')

    const handleMenuClose = () => {
        setAnchorEl(null)
        setSelectedMenu('')
    }

    return (
        <Menu
            keepMounted
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            getContentAnchorEl={null}
            id="fade-menu"
            onClose={handleMenuClose}
            open={Boolean(anchorEl)}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            <DisplayMenu selectedMenu={selectedMenu} setSelectedMenu={setSelectedMenu} />
        </Menu>
    )
}

export default MenuTopBar
